import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { fetchAvailableIndicators } from '../api/api';
import { getIndicatorKey } from '../utils/indicatorUtils';
import API_BASE_URL, { WS_BASE_URL } from '../api/config';

// Add time normalization helpers
const toSeconds = (t) => {
    if (t == null) return t;
    // If it's already a number, convert ms->s if necessary
    if (typeof t === 'number') {
        return t > 1000000000000 ? Math.floor(t / 1000) : t; // ms to s
    }
    // If it's a Date object
    if (t instanceof Date) {
        return Math.floor(t.getTime() / 1000);
    }
    // If it's a business day or plain object like { year, month, day }
    if (typeof t === 'object') {
        const { year, month, day } = t;
        if (typeof year === 'number' && typeof month === 'number' && typeof day === 'number') {
            return Math.floor(new Date(year, month - 1, day).getTime() / 1000);
        }
        return undefined; // unknown object format
    }
    // If it's a string, try parsing
    if (typeof t === 'string') {
        const ms = Date.parse(t);
        if (!Number.isNaN(ms)) return Math.floor(ms / 1000);
        const asNum = Number(t);
        if (!Number.isNaN(asNum)) return toSeconds(asNum);
    }
    return undefined;
};

const normalizeCandle = (c) => (c ? { ...c, time: toSeconds(c.time) } : c);

const normalizeIndicatorPayload = (data) => {
    if (Array.isArray(data)) {
        return data.map(p => ({ ...p, time: toSeconds(p.time) }));
    }
    if (data && typeof data === 'object') {
        const out = {};
        Object.keys(data).forEach(key => {
            const arr = data[key];
            out[key] = Array.isArray(arr) ? arr.map(p => ({ ...p, time: toSeconds(p.time) })) : arr;
        });
        return out;
    }
    return data;
};

// Merge helpers for indicator series
function mergePointPreferDefined(a, b) {
    // both have same time, choose one with defined numeric value; if both defined, prefer b (incoming)
    const av = (a && typeof a.value === 'number' && Number.isFinite(a.value)) ? a.value : undefined;
    const bv = (b && typeof b.value === 'number' && Number.isFinite(b.value)) ? b.value : undefined;
    if (bv !== undefined) return { time: b.time, value: b.value };
    if (av !== undefined) return { time: a.time, value: a.value };
    return { time: a.time }; // whitespace
}

function mergeSeriesArray(existing, incoming) {
    const ex = Array.isArray(existing) ? existing : [];
    const inc = Array.isArray(incoming) ? incoming : [];
    if (ex.length === 0) return inc.slice();
    if (inc.length === 0) return ex.slice();
    // Both sorted by time asc; merge like merge-sort with dedup by time
    const out = [];
    let i = 0, j = 0;
    while (i < ex.length && j < inc.length) {
        const a = ex[i], b = inc[j];
        if (a.time < b.time) { out.push(a); i++; }
        else if (a.time > b.time) { out.push(b); j++; }
        else { out.push(mergePointPreferDefined(a, b)); i++; j++; }
    }
    while (i < ex.length) out.push(ex[i++]);
    while (j < inc.length) out.push(inc[j++]);
    return out;
}

function mergeSeriesObject(existingObj, incomingObj) {
    const ex = (existingObj && typeof existingObj === 'object') ? existingObj : {};
    const inc = (incomingObj && typeof incomingObj === 'object') ? incomingObj : {};
    const keys = new Set([...Object.keys(ex), ...Object.keys(inc)]);
    const out = {};
    keys.forEach((k) => {
        out[k] = mergeSeriesArray(ex[k], inc[k]);
    });
    return out;
}

import { compressGaps, sortAndDeduplicateBars, TF_SECONDS } from '../utils/priceUtils';

const noop = () => {};
const defaultPriceFeedContext = {
    isConnected: false,
    candle: null,
    bid: null,
    ask: null,
    marketDepth: null,
    marketDepthError: null,
    marketDepthStatus: 'idle',
    setMarketDepthEnabled: noop,
    historicalData: [],
    activeIndicators: [],
    setActiveIndicators: noop,
    availableIndicators: {},
    sendIndicatorAction: noop,
    indicatorLiveUpdate: null,
    isLoggingEnabled: false,
    toggleLogging: noop,
    loadMoreHistory: async () => ({ added: 0, total: 0 }),
    specStreams: {},
    subscribeSpec: noop,
    unsubscribeSpec: noop,
};
const PriceFeedContext = createContext(defaultPriceFeedContext);
const INITIAL_HISTORY_RETRY_DELAYS_MS = [0, 500, 1500, 3000];
const MIN_INITIAL_HISTORY_BARS_BY_TF = {
    W1: 156,
    MN: 60,
    MN1: 60,
};
const DEFAULT_MIN_INITIAL_HISTORY_BARS = 350;
const MAX_SPARSE_HISTORY_BACKFILLS = 5;
const INITIAL_HISTORY_COUNT_BY_TF = {
    W1: 156,
    MN: 60,
    MN1: 60,
};
const DEFAULT_INITIAL_HISTORY_COUNT = 350;
const BACKGROUND_HISTORY_TARGET_BY_TF = {
    W1: 260,
    MN: 120,
    MN1: 120,
};
const DEFAULT_BACKGROUND_HISTORY_TARGET = 1000;
const BACKGROUND_HISTORY_DELAY_MS = 1200;
const MAX_SCROLL_HISTORY_LOAD_STEP = 5000;
const CHART_HISTORY_CACHE_TTL_MS = 5 * 60 * 1000;
const MAX_CHART_HISTORY_CACHE_ENTRIES = 32;
const chartHistoryCache = new Map();

function getMinimumInitialHistoryBars(timeframe) {
    const key = String(timeframe || '').toUpperCase();
    return MIN_INITIAL_HISTORY_BARS_BY_TF[key] || DEFAULT_MIN_INITIAL_HISTORY_BARS;
}

function getInitialHistoryRequestCount(timeframe) {
    const key = String(timeframe || '').toUpperCase();
    return INITIAL_HISTORY_COUNT_BY_TF[key] || DEFAULT_INITIAL_HISTORY_COUNT;
}

function getBackgroundHistoryTargetBars(timeframe) {
    const key = String(timeframe || '').toUpperCase();
    return BACKGROUND_HISTORY_TARGET_BY_TF[key] || DEFAULT_BACKGROUND_HISTORY_TARGET;
}

function getSparseHistoryBackfillStep(timeframe, currentLength = 0) {
    const key = String(timeframe || '').toUpperCase();
    if (key === 'W1') return Math.max(260, 180 - currentLength);
    if (key === 'MN' || key === 'MN1') return Math.max(120, 72 - currentLength);
    return Math.max(500, DEFAULT_MIN_INITIAL_HISTORY_BARS - currentLength);
}

function getBackgroundHistoryBackfillStep(timeframe, currentLength = 0) {
    const target = getBackgroundHistoryTargetBars(timeframe);
    if (currentLength >= target) return 0;
    return Math.min(750, Math.max(250, target - currentLength));
}

function getHistoryLoadStep(step) {
    const parsed = Number(step);
    if (!Number.isFinite(parsed) || parsed <= 0) {
        return MAX_SCROLL_HISTORY_LOAD_STEP;
    }
    return Math.min(MAX_SCROLL_HISTORY_LOAD_STEP, Math.max(1, Math.floor(parsed)));
}

function buildChartHistoryCacheKey(accountId, symbol, timeframe) {
    return `${accountId || ''}|${String(symbol || '').toUpperCase()}|${String(timeframe || '').toUpperCase()}`;
}

function restoreCompressedBarsToRealTime(bars) {
    return (Array.isArray(bars) ? bars : []).map((bar) => ({
        ...bar,
        time: bar?.time_real ?? bar?.time,
    }));
}

function getLastBarSnapshot(bars) {
    if (!Array.isArray(bars) || bars.length === 0) return null;
    const lb = bars[bars.length - 1];
    return { time: lb.time, time_real: lb.time_real ?? lb.time };
}

function getCachedChartHistory(cacheKey) {
    const cached = chartHistoryCache.get(cacheKey);
    if (!cached) return null;
    if (Date.now() - cached.updatedAt > CHART_HISTORY_CACHE_TTL_MS) {
        chartHistoryCache.delete(cacheKey);
        return null;
    }
    chartHistoryCache.delete(cacheKey);
    chartHistoryCache.set(cacheKey, cached);
    return {
        ...cached,
        data: Array.isArray(cached.data) ? cached.data.map((bar) => ({ ...bar })) : [],
    };
}

function setCachedChartHistory(cacheKey, payload) {
    if (!cacheKey || !Array.isArray(payload?.data) || payload.data.length === 0) return;
    chartHistoryCache.set(cacheKey, {
        data: payload.data.map((bar) => ({ ...bar })),
        effectiveTimeframe: payload.effectiveTimeframe,
        lastRequestedCount: payload.lastRequestedCount,
        updatedAt: Date.now(),
    });
    while (chartHistoryCache.size > MAX_CHART_HISTORY_CACHE_ENTRIES) {
        const oldestKey = chartHistoryCache.keys().next().value;
        chartHistoryCache.delete(oldestKey);
    }
}

export const usePriceFeed = () => useContext(PriceFeedContext);

function resolveEffectiveTimeframe(requestedTimeframe, servedTimeframe) {
    const requested = String(requestedTimeframe || '').toUpperCase();
    const served = String(servedTimeframe || '').toUpperCase();
    return served || requested;
}

function shouldRetryInitialHistoryError(error) {
    const status = Number(error?.status);
    return !Number.isFinite(status) || status === 408 || status === 429 || status >= 500;
}

function mergeLiveBarIntoHistory(existing, incomingBar) {
    const prev = Array.isArray(existing) ? existing : [];
    const incomingRealTime = incomingBar?.time_real ?? incomingBar?.time;
    if (!Number.isFinite(incomingRealTime) || !Number.isFinite(incomingBar?.time)) {
        return prev;
    }

    let replaced = false;
    const next = prev.map((bar) => {
        const barRealTime = bar?.time_real ?? bar?.time;
        if (barRealTime === incomingRealTime || bar?.time === incomingBar.time) {
            replaced = true;
            return { ...bar, ...incomingBar };
        }
        return bar;
    });

    if (!replaced) {
        const lastBar = prev[prev.length - 1];
        const lastRealTime = lastBar?.time_real ?? lastBar?.time;
        if (Number.isFinite(lastRealTime) && incomingRealTime < lastRealTime) {
            return prev;
        }
        next.push(incomingBar);
    }

    return sortAndDeduplicateBars(next);
}

// Normalize indicators to a lowercase-keyed map: { [nameLower]: meta }
function buildIndicatorKey(name) {
    return String(name || '').toLowerCase();
}
function normalizeAvailableIndicators(raw) {
    if (!raw) return {};
    if (Array.isArray(raw)) {
        const map = {};
        raw.forEach((item) => {
            if (!item || typeof item !== 'object') return;
            const key = buildIndicatorKey(item.name || item.display_name || item.id);
            if (key) {
                map[key] = { ...item, name: item.name || item.display_name || item.id };
            }
        });
        return map;
    }
    if (typeof raw === 'object') {
        // Some APIs return { indicators: { name: meta, ... } }
        const src = raw.indicators && typeof raw.indicators === 'object' ? raw.indicators : raw;
        const map = {};
        Object.keys(src).forEach((k) => {
            const item = src[k];
            if (!item || typeof item !== 'object') return;
            const key = buildIndicatorKey(item.name || item.display_name || k);
            map[key] = { ...item, name: item.name || item.display_name || k };
        });
        return map;
    }
    return {};
}

const PriceFeedProvider = ({ children, accountId, symbol, timeframe }) => {
    const [candle, setCandle] = useState(null);
    const [bid, setBid] = useState(null);
    const [ask, setAsk] = useState(null);
    const [historicalData, setHistoricalData] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [isLoggingEnabled, setIsLoggingEnabled] = useState(false);
    const [marketDepth, setMarketDepth] = useState(null);
    const [marketDepthError, setMarketDepthError] = useState(null);
    const [marketDepthStatus, setMarketDepthStatus] = useState('idle');
    const [activeIndicators, setActiveIndicators] = useState([]);
    const activeIndicatorsRef = useRef([]);
    // Lightweight live indicator point update (avoids re-render storm from setActiveIndicators on every tick)
    const [indicatorLiveUpdate, setIndicatorLiveUpdate] = useState(null);
    // Track the last indicator update time to detect new-bar transitions
    const lastIndicatorUpdateTimeRef = useRef(null);

    // ---- Throttle high-frequency WS state updates to prevent render storms ----
    const pendingBidRef = useRef(null);
    const pendingAskRef = useRef(null);
    const pendingCandleRef = useRef(null);
    const pendingMarketDepthRef = useRef(null);
    const pendingHistoricalRef = useRef(null); // null = no pending update, otherwise the updater fn or value
    const throttleRafRef = useRef(null);
    const lastFlushRef = useRef(0);
    const connectionTokenRef = useRef(0);
    const previousFeedContextKeyRef = useRef('');
    const THROTTLE_MS = 100; // max flush frequency

    const flushPendingUpdates = useCallback(() => {
        throttleRafRef.current = null;
        lastFlushRef.current = Date.now();
        if (pendingBidRef.current !== null) {
            const val = pendingBidRef.current;
            pendingBidRef.current = null;
            setBid(val);
        }
        if (pendingAskRef.current !== null) {
            const val = pendingAskRef.current;
            pendingAskRef.current = null;
            setAsk(val);
        }
        if (pendingCandleRef.current !== null) {
            const val = pendingCandleRef.current;
            pendingCandleRef.current = null;
            setCandle(val);
        }
        if (pendingMarketDepthRef.current !== null) {
            const val = pendingMarketDepthRef.current;
            pendingMarketDepthRef.current = null;
            setMarketDepth(val);
        }
        if (pendingHistoricalRef.current !== null) {
            const updater = pendingHistoricalRef.current;
            pendingHistoricalRef.current = null;
            setHistoricalData((prev) => {
                const next = typeof updater === 'function' ? updater(prev) : updater;
                historicalDataRef.current = Array.isArray(next) ? next : [];
                return next;
            });
        }
    }, []);

    const scheduleFlush = useCallback(() => {
        if (throttleRafRef.current) return; // already scheduled
        const elapsed = Date.now() - lastFlushRef.current;
        if (elapsed >= THROTTLE_MS) {
            // Enough time passed — flush on next animation frame
            throttleRafRef.current = requestAnimationFrame(flushPendingUpdates);
        } else {
            // Too soon — delay flush
            throttleRafRef.current = setTimeout(() => {
                throttleRafRef.current = requestAnimationFrame(flushPendingUpdates);
            }, THROTTLE_MS - elapsed);
        }
    }, [flushPendingUpdates]);

    const clearPendingFeedUpdates = useCallback(() => {
        if (throttleRafRef.current) {
            cancelAnimationFrame(throttleRafRef.current);
            clearTimeout(throttleRafRef.current);
            throttleRafRef.current = null;
        }
        pendingBidRef.current = null;
        pendingAskRef.current = null;
        pendingCandleRef.current = null;
        pendingMarketDepthRef.current = null;
        pendingHistoricalRef.current = null;
    }, []);

    // Clean up throttle timer on unmount
    useEffect(() => {
        return () => {
            clearPendingFeedUpdates();
        };
    }, [clearPendingFeedUpdates]);
    const [availableIndicators, setAvailableIndicators] = useState({});
    const availableIndicatorsRef = useRef({});
    const wsRef = useRef(null);
    // Track all opened sockets so we can keep them alive across account switches and clean them up on unmount
    const socketsSetRef = useRef(new Set());
    // Reconnect state
    const reconnectTimerRef = useRef(null);
    const reconnectDelayRef = useRef(2000);
    const sentIndicatorIdsRef = useRef(new Map());
    const loadingIndicatorsRef = useRef(false);
    const pendingActionsRef = useRef([]);
    // For incremental history growth requests
    const loadMoreInFlightRef = useRef(false);
    const lastRequestedCountRef = useRef(0);
    const loadMoreHistoryRef = useRef(null);
    const sparseBackfillContextRef = useRef('');
    const sparseBackfillAttemptsRef = useRef(0);
    // Gate live updates until initial history is applied for the current context
    const historyReadyRef = useRef(false);
    // Track latest history length synchronously
    const historicalLenRef = useRef(0);
    const historicalDataRef = useRef([]);
    // Track the actual timeframe served by the backend when fallback data differs
    const effectiveTimeframeRef = useRef(timeframe);
    // Track the last compressed bar {time, time_real} so the WS handler can compute compressed time synchronously
    const lastBarRef = useRef(null);
    // Track last indicator hydration length to avoid redundant re-requests
    const lastIndicatorHydrationLenRef = useRef(0);
    // Nested spec (subscribe_spec) state: map of uid -> { data_keys, data }
    const [specStreams, setSpecStreams] = useState({});
    const specStreamsRef = useRef({});
    useEffect(() => { specStreamsRef.current = specStreams; }, [specStreams]);
    const activeSpecSubsRef = useRef(new Map()); // uid -> { spec, timeframe, count, attachToId? }
    const marketDepthRequestRef = useRef({ enabled: false, depth: 10 });

    const setMarketDepthEnabled = useCallback((enabled, depth = 10) => {
        const normalizedDepth = Math.max(1, Math.min(100, Number(depth) || 10));
        marketDepthRequestRef.current = { enabled: Boolean(enabled), depth: normalizedDepth };
        setMarketDepthError(null);
        setMarketDepthStatus(enabled ? 'subscribing' : 'idle');
        if (!enabled) setMarketDepth(null);
        const socket = wsRef.current;
        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({
                action: enabled ? 'subscribe_market_depth' : 'unsubscribe_market_depth',
                depth: normalizedDepth,
            }));
        }
    }, []);

    useEffect(() => {
        effectiveTimeframeRef.current = timeframe;
    }, [timeframe]);

    // Keep refs in sync with state
    useEffect(() => {
        activeIndicatorsRef.current = Array.isArray(activeIndicators) ? activeIndicators : [];
    }, [activeIndicators]);

    useEffect(() => {
        historicalDataRef.current = Array.isArray(historicalData) ? historicalData : [];
    }, [historicalData]);

    // Keep ref in sync
    useEffect(() => {
        availableIndicatorsRef.current = availableIndicators || {};
    }, [availableIndicators]);

    useEffect(() => {
        const loadInitialData = async () => {
            const token = localStorage.getItem("token");
            if (!token) {
                setAvailableIndicators({});
                availableIndicatorsRef.current = {};
                return;
            }
            try {
                loadingIndicatorsRef.current = true;
                const indicators = await fetchAvailableIndicators();
                const normalized = normalizeAvailableIndicators(indicators);
                setAvailableIndicators(normalized);
                availableIndicatorsRef.current = normalized;
            } catch (error) {
                console.error("Failed to load available indicators:", error);
            } finally {
                loadingIndicatorsRef.current = false;
            }
        };
        loadInitialData();
    }, []);

    const ensureIndicatorsLoaded = useCallback(async () => {
        if (availableIndicatorsRef.current && Object.keys(availableIndicatorsRef.current).length > 0) return;
        if (loadingIndicatorsRef.current) {
            await new Promise((resolve) => {
                const start = Date.now();
                const check = () => {
                    if (!loadingIndicatorsRef.current || (availableIndicatorsRef.current && Object.keys(availableIndicatorsRef.current).length > 0)) return resolve();
                    if (Date.now() - start > 5000) return resolve();
                    setTimeout(check, 50);
                };
                check();
            });
            return;
        }
        try {
            loadingIndicatorsRef.current = true;
            const indicators = await fetchAvailableIndicators();
            const normalized = normalizeAvailableIndicators(indicators);
            setAvailableIndicators(normalized);
            availableIndicatorsRef.current = normalized;
        } catch (e) {
            console.error('Failed to fetch available indicators on demand:', e);
        } finally {
            loadingIndicatorsRef.current = false;
        }
    }, []);

    const sendIndicatorAction = useCallback((action, payload) => {
        const socket = wsRef.current;
        const message = { action, ...payload };
        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify(message));
            if (isLoggingEnabled) {
                console.log(`Sent indicator action '${action}':`, payload);
            }
        } else {
            pendingActionsRef.current.push(message);
            if (isLoggingEnabled) {
                console.log(`Queued indicator action '${action}' (socket not open yet).`);
            }
        }
    }, [isLoggingEnabled]);

    useEffect(() => {
        const connectionToken = connectionTokenRef.current + 1;
        connectionTokenRef.current = connectionToken;
        const abortController = new AbortController();
        const feedContextKey = `${accountId || ''}|${symbol || ''}|${timeframe || ''}`;
        const feedContextChanged = previousFeedContextKeyRef.current !== feedContextKey;
        previousFeedContextKeyRef.current = feedContextKey;
        let wsForEffect = null;
        let historyReadyTimer = null;
        let backgroundHistoryTimer = null;
        let heartbeatTimer = null;

        const isCurrentConnection = () => (
            connectionTokenRef.current === connectionToken && !abortController.signal.aborted
        );

        const closeSocket = (socket) => {
            if (!socket) return;
            if (socket === wsForEffect && heartbeatTimer) {
                clearInterval(heartbeatTimer);
                heartbeatTimer = null;
            }
            try {
                if (socket.readyState === WebSocket.OPEN) {
                    socket.send(JSON.stringify({ action: 'unsubscribe' }));
                }
            } catch (_) {}
            try {
                socket.close();
            } catch (_) {}
            socketsSetRef.current.delete(socket);
            if (wsRef.current === socket) {
                wsRef.current = null;
            }
        };

        const connectAndSubscribe = async (options = {}) => {
            const preserveState = options.preserveState === true;

            if (!accountId || !symbol || !timeframe) {
                clearPendingFeedUpdates();
                historyReadyRef.current = false;
                historicalLenRef.current = 0;
                lastBarRef.current = null;
                setCandle(null);
                setBid(null);
                setAsk(null);
                setMarketDepth(null);
                setMarketDepthError(null);
                setMarketDepthStatus('idle');
                setHistoricalData([]);
                historicalDataRef.current = [];
                setSpecStreams({});
                setIsConnected(false);
                return;
            }

            await ensureIndicatorsLoaded();
            if (!isCurrentConnection()) return;

            // 1. The previous effect cleanup closes the old active socket before a new context connects.
            const historyCacheKey = buildChartHistoryCacheKey(accountId, symbol, timeframe);
            const cachedHistory = getCachedChartHistory(historyCacheKey);

            // 2. Clear transient data on context changes. On reconnects, keep the
            // current chart visible and reopen the socket immediately.
            clearPendingFeedUpdates();
            if (preserveState) {
                historyReadyRef.current = historicalLenRef.current > 0;
            } else {
                historyReadyRef.current = false;
                historicalLenRef.current = 0;
                lastBarRef.current = null;
                lastRequestedCountRef.current = 0;
                lastIndicatorHydrationLenRef.current = 0;
                lastIndicatorUpdateTimeRef.current = null;
                loadMoreInFlightRef.current = false;
                setCandle(null);
                setBid(null);
                setAsk(null);
                setMarketDepth(null);
                setMarketDepthError(null);
                setMarketDepthStatus(
                    marketDepthRequestRef.current.enabled ? 'subscribing' : 'idle'
                );
                if (cachedHistory?.data?.length) {
                    const cachedData = cachedHistory.data;
                    setHistoricalData(cachedData);
                    historicalDataRef.current = cachedData;
                    historicalLenRef.current = cachedData.length;
                    effectiveTimeframeRef.current = cachedHistory.effectiveTimeframe || timeframe;
                    lastRequestedCountRef.current = cachedHistory.lastRequestedCount || cachedData.length;
                    lastBarRef.current = getLastBarSnapshot(cachedData);
                    historyReadyRef.current = true;
                } else {
                    setHistoricalData([]);
                    historicalDataRef.current = [];
                }
                setSpecStreams({});
                if (feedContextChanged) {
                    activeSpecSubsRef.current.clear();
                }
            }
            setIsConnected(false);
            if (!preserveState) {
                sentIndicatorIdsRef.current.clear();
                pendingActionsRef.current = [];
            }

            const authToken = localStorage.getItem("token");
            if (!authToken) {
                console.error("Authentication token not found.");
                return;
            }

            {
                // 3. Fetch historical data in the background. On reconnects,
                // this backfills candles missed while the socket was down.
                // The websocket opens immediately below so realtime is not
                // blocked by a slow broker/history request.
                (async () => {
                const params = new URLSearchParams({
                    account_id: accountId,
                    symbol: symbol,
                    resolution: timeframe,
                    count: String(getInitialHistoryRequestCount(timeframe)),
                });
                try {
                    // Cached history can be displayed immediately; otherwise
                    // live updates are accepted while history loads.
                    historyReadyRef.current = preserveState
                        ? historicalLenRef.current > 0
                        : !!cachedHistory?.data?.length;
                    let responseData = null;
                    for (let attempt = 0; attempt < INITIAL_HISTORY_RETRY_DELAYS_MS.length; attempt += 1) {
                        const delay = INITIAL_HISTORY_RETRY_DELAYS_MS[attempt];
                        if (delay > 0) {
                            await new Promise((resolve) => window.setTimeout(resolve, delay));
                            if (!isCurrentConnection()) return;
                        }

                        try {
                            const response = await fetch(`${API_BASE_URL}/api/price/chart/history/?${params.toString()}`, {
                                headers: { 'Authorization': `Bearer ${authToken}` },
                                signal: abortController.signal,
                            });
                            if (!isCurrentConnection()) return;
                            if (!response.ok) {
                                const httpError = new Error(`HTTP error! status: ${response.status}`);
                                httpError.status = response.status;
                                throw httpError;
                            }
                            responseData = await response.json();
                            break;
                        } catch (error) {
                            if (!isCurrentConnection() || error?.name === 'AbortError') return;
                            const canRetry = attempt < INITIAL_HISTORY_RETRY_DELAYS_MS.length - 1
                                && shouldRetryInitialHistoryError(error);
                            if (!canRetry) throw error;
                            console.warn('Retrying initial chart history fetch after error:', error);
                        }
                    }
                    if (!isCurrentConnection()) return;
                    if (!responseData) throw new Error('No chart history response received.');
                    // Detect if server fell back to a different timeframe (e.g. D1 when M30 was requested)
                    if (responseData.served_timeframe && responseData.served_timeframe !== timeframe) {
                        console.warn(`Price service served ${responseData.served_timeframe} instead of requested ${timeframe}`);
                    }
                    effectiveTimeframeRef.current = resolveEffectiveTimeframe(timeframe, responseData.served_timeframe);
                    const formattedData = Array.isArray(responseData.candles) ? sortAndDeduplicateBars(responseData.candles
                        .map(d => ({
                            time: toSeconds(d.time),
                            open: d.open,
                            high: d.high,
                            low: d.low,
                            close: d.close,
                            // passthrough volume if API provides it
                            volume: (Number.isFinite(d.volume) ? Number(d.volume) : (Number.isFinite(d.tick_volume) ? Number(d.tick_volume) : (Number.isFinite(d.tickVolume) ? Number(d.tickVolume) : undefined))),
                            tickVolume: (Number.isFinite(d.tick_volume) ? Number(d.tick_volume) : (Number.isFinite(d.tickVolume) ? Number(d.tickVolume) : undefined)),
                        }))
                        .filter(d => typeof d.time === 'number' && Number.isFinite(d.time))) : [];
                    const sourceData = cachedHistory?.data?.length
                        ? [...restoreCompressedBarsToRealTime(cachedHistory.data), ...formattedData]
                        : formattedData;
                    const liveData = restoreCompressedBarsToRealTime(historicalDataRef.current);
                    const combinedData = sortAndDeduplicateBars([
                        ...sourceData,
                        ...liveData,
                    ]);
                    const compressed = compressGaps(combinedData, effectiveTimeframeRef.current);
                    setHistoricalData(compressed);
                    historicalDataRef.current = compressed;
                    historicalLenRef.current = compressed.length;
                    lastBarRef.current = getLastBarSnapshot(compressed);
                    lastRequestedCountRef.current = Math.max(
                        cachedHistory?.lastRequestedCount || 0,
                        formattedData.length,
                        compressed.length
                    );
                    setCachedChartHistory(historyCacheKey, {
                        data: compressed,
                        effectiveTimeframe: effectiveTimeframeRef.current,
                        lastRequestedCount: lastRequestedCountRef.current,
                    });
                    // Allow a tick so consumers (e.g., chart) can ingest history before live updates arrive
                    clearTimeout(historyReadyTimer);
                    historyReadyTimer = setTimeout(() => {
                        if (isCurrentConnection()) {
                            historyReadyRef.current = historicalLenRef.current > 0;
                        }
                    }, 0);
                    // Backfill a moderate amount after first paint; user scroll remains the primary deep-history trigger.
                    const backgroundStep = getBackgroundHistoryBackfillStep(effectiveTimeframeRef.current, compressed.length);
                    if (formattedData.length > 0 && backgroundStep > 0) {
                        clearTimeout(backgroundHistoryTimer);
                        backgroundHistoryTimer = setTimeout(() => {
                            if (isCurrentConnection()) loadMoreHistoryRef.current?.(backgroundStep);
                        }, BACKGROUND_HISTORY_DELAY_MS);
                    }
                } catch (error) {
                    if (!isCurrentConnection() || error?.name === 'AbortError') return;
                    console.error('Error fetching historical data:', error);
                    return;
                }
                })();
            }

            // 4. Establish new WebSocket connection immediately
            if (!isCurrentConnection()) return;
            const origin = WS_BASE_URL || (() => {
                const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
                return `${proto}://${window.location.host}`;
            })();
            const wsUrl = `${origin}/ws/prices/${accountId}/${symbol}/?token=${authToken}`;
            const ws = new WebSocket(wsUrl);
            wsForEffect = ws;
            wsRef.current = ws;
            socketsSetRef.current.add(ws);

            ws.onopen = () => {
                if (!isCurrentConnection() || wsRef.current !== ws) {
                    closeSocket(ws);
                    return;
                }
                setIsConnected(true);
                reconnectDelayRef.current = 2000; // reset backoff on successful connect
                console.log(`Price socket connected for ${symbol}@${timeframe}.`);
                
                const subscriptionMessage = { action: 'subscribe', timeframe };
                if (isLoggingEnabled) {
                    console.log(`Attempting to subscribe to ${symbol} ${timeframe} candles.`, {
                        url: wsUrl,
                        message: subscriptionMessage,
                    });
                }
                ws.send(JSON.stringify(subscriptionMessage));
                if (marketDepthRequestRef.current.enabled) {
                    ws.send(JSON.stringify({
                        action: 'subscribe_market_depth',
                        depth: marketDepthRequestRef.current.depth,
                    }));
                    setMarketDepthStatus('subscribing');
                }
                clearInterval(heartbeatTimer);
                heartbeatTimer = setInterval(() => {
                    try {
                        if (wsRef.current === ws && ws.readyState === WebSocket.OPEN) {
                            ws.send(JSON.stringify({ action: 'heartbeat', ts: Date.now() }));
                        }
                    } catch (_) {}
                }, 60_000);
                if (isLoggingEnabled) {
                    console.log(`Subscribing to live price and ${timeframe} candle updates for ${symbol}.`);
                }

                // Re-add any existing indicators
                activeIndicators.forEach(indicator => {
                    const key = getIndicatorKey(indicator.name, indicator.params, symbol, timeframe, indicator.paneType);
                    const msg = {
                        action: 'add_indicator',
                        indicator: indicator.name,
                        params: indicator.params,
                        unique_id: key,
                        count: 2700,
                        pane_type: indicator.paneType,
                        scale_type: indicator.scaleType
                    };
                    ws.send(JSON.stringify(msg));
                });

                // Flush any buffered actions queued before the socket was open
                if (pendingActionsRef.current.length > 0) {
                    for (const msg of pendingActionsRef.current) {
                        ws.send(JSON.stringify(msg));
                    }
                    pendingActionsRef.current = [];
                }

                // Re-subscribe any active nested spec subscriptions (those weren't queued to avoid duplicates)
                try {
                    activeSpecSubsRef.current.forEach((sub, uid) => {
                        const payload = {
                            type: 'subscribe_spec',
                            unique_id: uid,
                            spec: sub.spec,
                            timeframe: sub.timeframe || timeframe,
                            count: sub.count || 500,
                        };
                        ws.send(JSON.stringify(payload));
                        if (isLoggingEnabled) console.log('Re-subscribed spec', payload);
                    });
                } catch (e) {
                    console.warn('Failed to re-subscribe specs:', e);
                }
            };

            ws.onclose = (evt) => {
                clearInterval(heartbeatTimer);
                heartbeatTimer = null;
                socketsSetRef.current.delete(ws);
                if (!isCurrentConnection()) {
                    console.log('Price socket disconnected (stale, not reconnecting).');
                    return;
                }
                setIsConnected(false);
                // Auto-reconnect if this is still the active socket
                if (wsRef.current === ws && accountId && symbol && timeframe) {
                    const delay = evt?.code === 4000 ? 100 : reconnectDelayRef.current;
                    console.log(`Price socket disconnected (code=${evt?.code}). Reconnecting in ${delay}ms…`);
                    clearTimeout(reconnectTimerRef.current);
                    reconnectTimerRef.current = setTimeout(() => {
                        connectAndSubscribe({ preserveState: true });
                    }, delay);
                    reconnectDelayRef.current = Math.min(delay * 1.5, 30000);
                } else {
                    console.log('Price socket disconnected (stale, not reconnecting).');
                }
            };
            ws.onerror = () => {
                if (isCurrentConnection() && wsRef.current === ws) setIsConnected(false);
            };

            ws.onmessage = (event) => {
                // Ignore messages from stale sockets
                if (!isCurrentConnection() || wsRef.current !== ws) return;
                try {
                    const message = JSON.parse(event.data.replace(/NaN/g, 'null'));
                    if (isLoggingEnabled) console.log('WS Message:', message);

                    if (message.type === 'new_candle') {
                        const msgAcct = (message.account_id || message.data?.account_id);
                        // Account mismatch can happen if backend uses a different identifier; don't drop, just log.
                        if (msgAcct && String(msgAcct) !== String(accountId)) {
                            if (isLoggingEnabled) console.warn('Account mismatch on new_candle (proceeding):', { msgAcct, expected: accountId });
                        }
                        const msgSym = (message.symbol || message.data?.symbol || message.instrument || message.data?.instrument);
                        if (msgSym && String(msgSym).toUpperCase() !== String(symbol).toUpperCase()) {
                            if (isLoggingEnabled) console.log('Dropped new_candle for other symbol:', msgSym);
                            return;
                        }
                        const nc = normalizeCandle(message.data);
                        if (nc && typeof nc.time === 'number') {
                            // Compute compressed time synchronously from lastBarRef
                            const last = lastBarRef.current;
                            const interval = TF_SECONDS[(effectiveTimeframeRef.current || timeframe || '').toUpperCase()] || 300;
                            const gapThreshold = interval * 3;
                            let compressedTime = nc.time;
                            if (last && typeof last.time_real === 'number') {
                                if (last.time_real === nc.time) {
                                    // Same bar update — keep existing compressed time
                                    compressedTime = last.time;
                                } else if (nc.time > last.time_real) {
                                    // New bar — compress gap if needed
                                    const gap = nc.time - last.time_real;
                                    compressedTime = gap > gapThreshold ? last.time + interval : last.time + gap;
                                }
                            }
                            const d = message?.data;
                            const vol = Number.isFinite(d?.volume) ? Number(d.volume) : (Number.isFinite(d?.tick_volume) ? Number(d.tick_volume) : (Number.isFinite(d?.tickVolume) ? Number(d.tickVolume) : undefined));
                            const tVol = Number.isFinite(d?.tick_volume) ? Number(d.tick_volume) : (Number.isFinite(d?.tickVolume) ? Number(d.tickVolume) : undefined);

                            // Update lastBarRef synchronously so next WS message sees the correct state
                            lastBarRef.current = { time: compressedTime, time_real: nc.time };

                            // Buffer candle update (throttled to prevent render storms)
                            pendingCandleRef.current = { ...nc, time: compressedTime, time_real: nc.time };

                            // Buffer historicalData update (throttled)
                            // Capture the updater as a closure; on flush the latest one wins
                            const histUpdater = (prev) => {
                                const incomingBar = {
                                    time: compressedTime,
                                    time_real: nc.time,
                                    open: d?.open,
                                    high: d?.high,
                                    low: d?.low,
                                    close: d?.close,
                                    volume: vol,
                                    tickVolume: tVol,
                                };
                                const next = mergeLiveBarIntoHistory(prev, incomingBar);
                                historicalLenRef.current = next.length;
                                return next;
                            };
                            pendingHistoricalRef.current = histUpdater;
                            scheduleFlush();
                        }
                    } else if (message.type === 'market_depth') {
                        const data = message.data || {};
                        const msgSym = data.symbol || message.symbol;
                        if (msgSym && String(msgSym).toUpperCase() !== String(symbol).toUpperCase()) {
                            return;
                        }
                        pendingMarketDepthRef.current = data;
                        setMarketDepthError(null);
                        setMarketDepthStatus('streaming');
                        scheduleFlush();
                    } else if (message.type === 'market_depth_error') {
                        setMarketDepthError(message.error || 'Market depth is unavailable');
                        setMarketDepthStatus('error');
                    } else if (message.type === 'market_depth_unsubscribed') {
                        setMarketDepth(null);
                        setMarketDepthStatus('idle');
                    } else if (message.type === 'live_price') {
                        const msgAcct = (message.account_id || message.data?.account_id);
                        // Account mismatch can happen if backend uses a different identifier; don't drop, just log.
                        if (msgAcct && String(msgAcct) !== String(accountId)) {
                            if (isLoggingEnabled) console.warn('Account mismatch on live_price (proceeding):', { msgAcct, expected: accountId });
                        }
                        const msgSym = (message.symbol || message.data?.symbol || message.instrument || message.data?.instrument);
                        if (msgSym && String(msgSym).toUpperCase() !== String(symbol).toUpperCase()) {
                            if (isLoggingEnabled) console.log('Dropped live_price for other symbol:', msgSym);
                            return;
                        }
                        const nextBid = message?.data?.bid;
                        const nextAsk = message?.data?.ask;
                        // Buffer bid/ask updates (throttled to prevent render storms)
                        if (nextBid != null) pendingBidRef.current = nextBid;
                        if (nextAsk != null) pendingAskRef.current = nextAsk;
                        if (nextBid != null || nextAsk != null) scheduleFlush();
                    } else if (message.type === 'indicator_data') {
                        const msgAcct = (message.account_id || message.data?.account_id);
                        // Account mismatch can happen if backend uses a different identifier; don't drop, just log.
                        if (msgAcct && String(msgAcct) !== String(accountId)) {
                            if (isLoggingEnabled) console.warn('Account mismatch on indicator_data (proceeding):', { msgAcct, expected: accountId });
                        }
                        const msgSym = (message.symbol || message.data?.symbol || message.instrument || message.data?.instrument);
                        if (msgSym && String(msgSym).toUpperCase() !== String(symbol).toUpperCase()) {
                            if (isLoggingEnabled) console.log('Dropped indicator_data for other symbol:', msgSym);
                            return;
                        }
                        const { indicator, data, unique_id: backendId, params, data_keys } = message;
                        const infoKey = buildIndicatorKey(indicator);
                        let indicatorInfo = availableIndicatorsRef.current[infoKey];
                        if (!indicatorInfo) {
                            // In the unlikely event metadata still isn't ready, try once more lazily
                            if (isLoggingEnabled) console.warn('Indicator metadata missing; attempting lazy load.');
                            ensureIndicatorsLoaded().then(() => {
                                const refreshed = availableIndicatorsRef.current[infoKey];
                                if (!refreshed) return;
                                const stableKey = getIndicatorKey(indicator, params, symbol, timeframe, refreshed.pane_type);
                                const normalizedData = normalizeIndicatorPayload(data);
                                setActiveIndicators(prev => {
                                    const newIndicators = [...prev];
                                    const idx = newIndicators.findIndex(i => i.id === stableKey);
                                    if (idx !== -1) {
                                        newIndicators[idx] = { ...newIndicators[idx], data: normalizedData, backendUniqueId: backendId, data_keys };
                                    } else {
                                        newIndicators.push({
                                            id: stableKey,
                                            backendUniqueId: backendId,
                                            name: indicator,
                                            data: normalizedData,
                                            paneType: refreshed.pane_type,
                                            scaleType: refreshed.scale_type,
                                            params,
                                            data_keys
                                        });
                                    }
                                    sentIndicatorIdsRef.current.set(backendId, stableKey);
                                    // Sync ref immediately so indicator_update handler can find it
                                    activeIndicatorsRef.current = newIndicators;
                                    return newIndicators;
                                });
                            });
                            return;
                        }

                        const stableKey = getIndicatorKey(indicator, params, symbol, timeframe, indicatorInfo.pane_type);
                        const normalizedData = normalizeIndicatorPayload(data);
                        
                        setActiveIndicators(prev => {
                            const newIndicators = [...prev];
                            const idx = newIndicators.findIndex(i => i.id === stableKey);
                            if (idx !== -1) {
                                const current = newIndicators[idx];
                                const merged = Array.isArray(normalizedData)
                                    ? mergeSeriesArray(current.data, normalizedData)
                                    : mergeSeriesObject(current.data, normalizedData);
                                newIndicators[idx] = { ...current, data: merged, backendUniqueId: backendId, data_keys };
                            } else {
                                newIndicators.push({
                                    id: stableKey,
                                    backendUniqueId: backendId,
                                    name: indicator,
                                    data: normalizedData,
                                    paneType: indicatorInfo.pane_type,
                                    scaleType: indicatorInfo.scale_type,
                                    params,
                                    data_keys
                                });
                            }
                            sentIndicatorIdsRef.current.set(backendId, stableKey);
                            // Sync ref immediately so indicator_update handler can find it
                            activeIndicatorsRef.current = newIndicators;
                            return newIndicators;
                        });
                    } else if (message.type === 'indicator_removed') {
                        const { unique_id: backendId } = message;
                        const frontendId = sentIndicatorIdsRef.current.get(backendId);
                        if (frontendId) {
                            setActiveIndicators(prev => prev.filter(ind => ind.id !== frontendId));
                            // Also remove any nested spec streams attached to this indicator
                            try {
                                const streams = Object.entries(specStreamsRef.current || {});
                                streams.forEach(([uid, entry]) => {
                                    const pid = entry?.attachToId || entry?.attachToMeta?.parentId;
                                    const byMeta = entry?.attachToMeta ? (String(entry.attachToMeta.parentName||'').toLowerCase() === String(message.indicator||'').toLowerCase()) : false;
                                    if (pid === frontendId || byMeta) {
                                        // Remove from state and unsubscribe
                                        setSpecStreams(prev => {
                                            const { [uid]: _, ...rest } = prev || {};
                                            return rest;
                                        });
                                        activeSpecSubsRef.current.delete(uid);
                                        try {
                                            const socket = wsRef.current;
                                            const payload = { type: 'unsubscribe_spec', unique_id: uid };
                                            if (socket && socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify(payload));
                                        } catch {}
                                    }
                                });
                            } catch {}
                        }
                        sentIndicatorIdsRef.current.delete(backendId);
                    } else if (message.type === 'indicator_update') {
                        const msgAcct = (message.account_id || message.data?.account_id);
                        // Account mismatch can happen if backend uses a different identifier; don't drop, just log.
                        if (msgAcct && String(msgAcct) !== String(accountId)) {
                            if (isLoggingEnabled) console.warn('Account mismatch on indicator_update (proceeding):', { msgAcct, expected: accountId });
                        }
                        const msgSym = (message.symbol || message.data?.symbol || message.instrument || message.data?.instrument);
                        if (msgSym && String(msgSym).toUpperCase() !== String(symbol).toUpperCase()) {
                            if (isLoggingEnabled) console.log('Dropped indicator_update for other symbol:', msgSym);
                            return;
                        }
                        if (isLoggingEnabled) {
                            console.log('Indicator Update Received:', message);
                        }
                        const { unique_id: backendId, data: point, data_keys } = message;
                        const newPoint = { ...point, time: toSeconds(point?.time) };
                        const frontendId = sentIndicatorIdsRef.current.get(backendId);
                        if (frontendId && typeof newPoint.time === 'number') {
                            // Helper to derive value for a given key from message.data
                            const valueFor = (k) => {
                                if (!k) return undefined;
                                if (Object.prototype.hasOwnProperty.call(newPoint, k)) return newPoint[k];
                                if (newPoint.value && typeof newPoint.value === 'object' && Object.prototype.hasOwnProperty.call(newPoint.value, k)) return newPoint.value[k];
                                if (newPoint.outputs && typeof newPoint.outputs === 'object' && Object.prototype.hasOwnProperty.call(newPoint.outputs, k)) return newPoint.outputs[k];
                                return undefined;
                            };

                            // Detect new-bar transition (indicator time changed from last tick)
                            const isNewBar = lastIndicatorUpdateTimeRef.current != null && newPoint.time !== lastIndicatorUpdateTimeRef.current;
                            lastIndicatorUpdateTimeRef.current = newPoint.time;

                            // Mutate the ref's data in-place (no React re-render)
                            const ind = activeIndicatorsRef.current.find(i => i.id === frontendId);
                            if (ind) {
                                if (typeof ind.data === 'object' && !Array.isArray(ind.data) && ind.data !== null) {
                                    // Object-of-arrays (multi series) case — mutate in place
                                    const keys = Array.isArray(data_keys) && data_keys.length > 0
                                        ? data_keys
                                        : Object.keys(ind.data);
                                    keys.forEach((k) => {
                                        if (!Array.isArray(ind.data[k])) return;
                                        const v = valueFor(k);
                                        if (v == null || !Number.isFinite(v)) return;
                                        const series = ind.data[k];
                                        const last = series[series.length - 1];
                                        if (last && last.time === newPoint.time) {
                                            series[series.length - 1] = { time: newPoint.time, value: v };
                                        } else {
                                            series.push({ time: newPoint.time, value: v });
                                        }
                                    });
                                } else {
                                    // Array-of-points (single series) case — mutate in place
                                    const dataArr = Array.isArray(ind.data) ? ind.data : [];
                                    let value = undefined;
                                    if (Array.isArray(data_keys) && data_keys.length > 0) {
                                        value = valueFor(data_keys[0]);
                                    }
                                    if (value == null && Number.isFinite(newPoint.value)) value = newPoint.value;
                                    if (value == null) {
                                        for (const k of Object.keys(newPoint)) {
                                            if (k === 'time') continue;
                                            const val = newPoint[k];
                                            if (Number.isFinite(val)) { value = val; break; }
                                        }
                                    }
                                    if (Number.isFinite(value)) {
                                        const last = dataArr[dataArr.length - 1];
                                        if (last && last.time === newPoint.time) {
                                            dataArr[dataArr.length - 1] = { time: newPoint.time, value };
                                        } else {
                                            dataArr.push({ time: newPoint.time, value });
                                        }
                                    }
                                }
                            }

                            // On new-bar transition, sync ref data to state so full re-renders
                            // include closed-bar indicator values (happens once per bar, not per tick)
                            if (isNewBar) {
                                const synced = activeIndicatorsRef.current.map(i => ({
                                    ...i,
                                    data: Array.isArray(i.data) ? [...i.data]
                                        : (i.data && typeof i.data === 'object')
                                            ? Object.fromEntries(Object.entries(i.data).map(([k, v]) => [k, Array.isArray(v) ? [...v] : v]))
                                            : i.data,
                                }));
                                activeIndicatorsRef.current = synced;
                                setActiveIndicators(synced);
                            }

                            // Emit lightweight live update for TradingChart to handle via series.update()
                            setIndicatorLiveUpdate({ id: frontendId, point: newPoint, data_keys, ts: Date.now() });
                        }
                        } else if (message.type === 'spec_data') {
                            const { unique_id: uid, data, data_keys } = message;
                            // Ignore late/rogue data for specs we are not tracking anymore
                            if (!activeSpecSubsRef.current.has(uid)) {
                                if (isLoggingEnabled) console.warn('Dropping spec_data for unknown uid', uid);
                                return;
                            }
                            // Normalize times
                            const normalized = Array.isArray(data)
                                ? data.map(p => ({ ...p, time: toSeconds(p.time) }))
                                : (data && typeof data === 'object')
                                    ? Object.fromEntries(Object.entries(data).map(([k, arr]) => [k, (Array.isArray(arr) ? arr.map(p => ({ ...p, time: toSeconds(p.time) })) : arr)]))
                                    : data;
                            setSpecStreams(prev => {
                                const meta = activeSpecSubsRef.current.get(uid) || {};
                                return {
                                    ...prev,
                                    [uid]: { ...(prev[uid] || {}), attachToId: meta.attachToId, attachToMeta: meta.attachToMeta, data_keys: Array.isArray(data_keys) ? data_keys : ['value'], data: normalized },
                                };
                            });
                        } else if (message.type === 'spec_update') {
                            // Optionally gate spec updates until history is ready
                            if (!historyReadyRef.current) {
                                if (isLoggingEnabled) console.log('Dropped spec_update before history ready');
                                return;
                            }
                            const { unique_id: uid, data: point, data_keys } = message;
                            const t = toSeconds(point?.time);
                            if (typeof t !== 'number') return;
                            setSpecStreams(prev => {
                                const entry = prev[uid];
                                if (!entry) return prev; // wait for seed data
                                const keys = Array.isArray(data_keys) && data_keys.length > 0 ? data_keys : (Array.isArray(entry.data_keys) && entry.data_keys.length > 0 ? entry.data_keys : ['value']);
                                // Helper to read a value from the update for key k
                                const valueFor = (k) => {
                                    if (!k) return undefined;
                                    if (Object.prototype.hasOwnProperty.call(point, k)) return point[k];
                                    if (point.value && typeof point.value === 'object' && Object.prototype.hasOwnProperty.call(point.value, k)) return point.value[k];
                                    if (point.outputs && typeof point.outputs === 'object' && Object.prototype.hasOwnProperty.call(point.outputs, k)) return point.outputs[k];
                                    if (typeof point.value === 'number') return point.value; // fallback for single series
                                    return undefined;
                                };
                                let nextEntry = entry;
                                if (Array.isArray(entry.data)) {
                                    // Single-series
                                    let v = ('value' in point && Number.isFinite(point.value)) ? point.value : undefined;
                                    if (v == null && keys.length > 0) {
                                        const k0 = valueFor(keys[0]);
                                        if (Number.isFinite(k0)) v = k0;
                                    }
                                    if (!Number.isFinite(v)) return prev;
                                    const arr = entry.data;
                                    const last = arr[arr.length - 1];
                                    const nextPoint = { time: t, value: v };
                                    const nextArr = last && last.time === t ? [...arr.slice(0, -1), nextPoint] : [...arr, nextPoint];
                                    nextEntry = { ...entry, data_keys: keys, data: nextArr };
                                } else if (entry.data && typeof entry.data === 'object') {
                                    // Multi-series
                                    const updated = { ...entry.data };
                                    let changed = false;
                                    keys.forEach((k) => {
                                        const v = valueFor(k);
                                        if (v == null || !Number.isFinite(v)) return;
                                        const series = Array.isArray(updated[k]) ? updated[k] : [];
                                        const last = series[series.length - 1];
                                        if (last && last.time === t) {
                                            updated[k] = [...series.slice(0, -1), { time: t, value: v }];
                                        } else {
                                            updated[k] = [...series, { time: t, value: v }];
                                        }
                                        changed = true;
                                    });
                                    if (!changed) return prev;
                                    nextEntry = { ...entry, data_keys: keys, data: updated };
                                }
                                if (nextEntry === entry) return prev;
                                return { ...prev, [uid]: nextEntry };
                            });
                    } else if (message.error) {
                        console.error('WS Error:', message.error);
                        // If the broker/subscribe failed, suppress auto-reconnect —
                        // reconnecting immediately won't help and just spins the loop.
                        const errStr = String(message.error).toLowerCase();
                        if (errStr.includes('subscribe failed') || errStr.includes('broker') || errStr.includes('mt5') || errStr.includes('connection aborted')) {
                            console.warn('Broker unavailable — pausing WS reconnect (will retry in 60s).');
                            // Detach so onclose doesn't trigger immediate reconnect
                            wsRef.current = null;
                            clearTimeout(reconnectTimerRef.current);
                            // Schedule a single slow retry
                            reconnectTimerRef.current = setTimeout(() => {
                                connectAndSubscribe({ preserveState: true });
                            }, 60_000);
                        }
                    }
                } catch (error) {
                    console.error("Failed to parse WS message:", error, event.data);
                }
            };
        };

        connectAndSubscribe();

    // Cancel any pending reconnect when deps change (new connection will be established)
    return () => {
        abortController.abort();
        if (connectionTokenRef.current === connectionToken) {
            connectionTokenRef.current += 1;
        }
        clearTimeout(historyReadyTimer);
        clearTimeout(backgroundHistoryTimer);
        clearInterval(heartbeatTimer);
        clearTimeout(reconnectTimerRef.current);
        clearPendingFeedUpdates();
        historyReadyRef.current = false;
        closeSocket(wsForEffect);
    };
    }, [accountId, symbol, timeframe, isLoggingEnabled, clearPendingFeedUpdates, ensureIndicatorsLoaded]);

    // On provider unmount, browser close, or logout — gracefully close all tracked sockets
    useEffect(() => {
        const closeAllSockets = () => {
            try {
                socketsSetRef.current.forEach((ws) => {
                    try {
                        if (ws && ws.readyState === WebSocket.OPEN) {
                            ws.send(JSON.stringify({ action: 'unsubscribe' }));
                        }
                    } catch (_) {}
                    try {
                        ws && ws.close();
                    } catch (_) {}
                });
            } finally {
                socketsSetRef.current.clear();
                // Prevent onclose from scheduling a stale reconnect
                wsRef.current = null;
                clearTimeout(reconnectTimerRef.current);
            }
        };
        window.addEventListener('beforeunload', closeAllSockets);
        window.addEventListener('valpha:logout', closeAllSockets);
        return () => {
            window.removeEventListener('beforeunload', closeAllSockets);
            window.removeEventListener('valpha:logout', closeAllSockets);
            // Full provider teardown (app unmount) — also clean up
            closeAllSockets();
        };
    }, []);

    const toggleLogging = useCallback(() => setIsLoggingEnabled(prev => !prev), []);

    // Public helpers to manage nested spec subscriptions
    const subscribeSpec = useCallback((uniqueId, spec, opts = {}) => {
        if (!uniqueId || !spec) return;
        const payload = {
            type: 'subscribe_spec',
            unique_id: uniqueId,
            spec,
            timeframe: opts.timeframe || timeframe,
            count: opts.count || 500,
        };
        // Track desired subscription for re-subscribe on reconnect
        activeSpecSubsRef.current.set(uniqueId, { spec, timeframe: payload.timeframe, count: payload.count, attachToId: opts.attachToId, attachToMeta: opts.attachToMeta });
        if (opts.attachToId || opts.attachToMeta) {
            setSpecStreams(prev => ({ ...prev, [uniqueId]: { ...(prev[uniqueId] || {}), attachToId: opts.attachToId, attachToMeta: opts.attachToMeta } }));
        }
        const socket = wsRef.current;
        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify(payload));
            if (isLoggingEnabled) console.log('Sent subscribe_spec', payload);
        } else {
            // Do NOT queue subscribe_spec to avoid duplicate subscribes; we'll re-send on ws open from activeSpecSubsRef
            if (isLoggingEnabled) console.log('WS not open; will re-subscribe_spec on open', { uniqueId });
        }
    }, [timeframe, isLoggingEnabled]);

    const unsubscribeSpec = useCallback((uniqueId) => {
        if (!uniqueId) return;
        activeSpecSubsRef.current.delete(uniqueId);
        setSpecStreams(prev => {
            const { [uniqueId]: _, ...rest } = prev || {};
            return rest;
        });
        const payload = { type: 'unsubscribe_spec', unique_id: uniqueId };
        const socket = wsRef.current;
        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify(payload));
            if (isLoggingEnabled) console.log('Sent unsubscribe_spec', payload);
        } else {
            pendingActionsRef.current.push(payload);
            if (isLoggingEnabled) console.log('Queued unsubscribe_spec (socket not open)', payload);
        }
    }, [isLoggingEnabled]);

    // Re-subscribe all nested specs on timeframe change so overlays persist across TF switches
    useEffect(() => {
        const socket = wsRef.current;
        activeSpecSubsRef.current.forEach((meta, uid) => {
            const payload = {
                type: 'subscribe_spec',
                unique_id: uid,
                spec: meta.spec,
                timeframe: timeframe,
                count: meta.count || 500,
            };
            // update stored timeframe for this spec
            activeSpecSubsRef.current.set(uid, { ...meta, timeframe });
            if (socket && socket.readyState === WebSocket.OPEN) {
                try { socket.send(JSON.stringify(payload)); } catch {}
            }
        });
    }, [timeframe]);

    // Load more historical data: prefer cursor-based prepend (before+limit), fallback to count-growth
    const loadMoreHistory = useCallback(async (step = MAX_SCROLL_HISTORY_LOAD_STEP) => {
        const requestStep = getHistoryLoadStep(step);
        const requestToken = connectionTokenRef.current;
        const isCurrentRequest = () => connectionTokenRef.current === requestToken;
        if (loadMoreInFlightRef.current) return { added: 0, total: historicalData.length };
        if (!accountId || !symbol || !timeframe) return { added: 0, total: historicalData.length };
        const token = localStorage.getItem('token');
        if (!token) return { added: 0, total: historicalData.length };

        const prev = Array.isArray(historicalData) ? historicalData : [];
        const prevLen = prev.length;
        const historyCacheKey = buildChartHistoryCacheKey(accountId, symbol, timeframe);
        // Use time_real (original timestamp before gap compression) for cursors if available
        const oldest = prevLen > 0 ? (prev[0]?.time_real ?? prev[0]?.time) : undefined;

        // Try cursor-based prepend first when we have an oldest bar
        if (typeof oldest === 'number' && Number.isFinite(oldest)) {
            loadMoreInFlightRef.current = true;
            try {
                const params = new URLSearchParams({
                    account_id: accountId,
                    symbol,
                    resolution: timeframe,
                    before: String(oldest),
                    limit: String(requestStep),
                });
                // Use sanitized API_BASE_URL (full origin) exported from config
                const base = API_BASE_URL.replace(/\/$/, '');
                const resp = await fetch(`${base}/api/price/chart/history/?${params.toString()}`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                if (!isCurrentRequest()) return { added: 0, total: prevLen };
                if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                const json = await resp.json();
                if (!isCurrentRequest()) return { added: 0, total: prevLen };
                effectiveTimeframeRef.current = resolveEffectiveTimeframe(timeframe, json?.served_timeframe || effectiveTimeframeRef.current);
                const raw = Array.isArray(json?.candles) ? json.candles : [];
                const chunk = sortAndDeduplicateBars(raw
                    .map(d => ({
                        time: toSeconds(d.time),
                        ...(d.time_real != null ? { time_real: toSeconds(d.time_real) } : {}),
                        open: d.open,
                        high: d.high,
                        low: d.low,
                        close: d.close,
                        volume: (Number.isFinite(d.volume) ? Number(d.volume) : (Number.isFinite(d.tick_volume) ? Number(d.tick_volume) : (Number.isFinite(d.tickVolume) ? Number(d.tickVolume) : undefined))),
                        tickVolume: (Number.isFinite(d.tick_volume) ? Number(d.tick_volume) : (Number.isFinite(d.tickVolume) ? Number(d.tickVolume) : undefined)),
                    }))
                    .filter(d => typeof d.time === 'number' && (d.time_real ?? d.time) < oldest));

                if (chunk.length > 0) {
                    // Restore prev bars to real timestamps for merge, then re-compress everything
                    const prevReal = prev.map(b => ({ ...b, time: b.time_real ?? b.time }));
                    const seenReal = new Set(prevReal.map(b => b.time));
                    const unique = chunk.filter(b => !seenReal.has(b.time));
                    if (unique.length > 0) {
                        const merged = [...unique, ...prevReal].sort((a, b) => a.time - b.time);
                        const compressed = compressGaps(merged, effectiveTimeframeRef.current);
                        const nextLen = compressed.length;
                        setHistoricalData(compressed);
                        historicalLenRef.current = nextLen;
                        lastBarRef.current = getLastBarSnapshot(compressed);
                        lastRequestedCountRef.current = Math.max(lastRequestedCountRef.current || 0, nextLen);
                        setCachedChartHistory(historyCacheKey, {
                            data: compressed,
                            effectiveTimeframe: effectiveTimeframeRef.current,
                            lastRequestedCount: lastRequestedCountRef.current,
                        });
                        // Re-hydrate indicators to cover the enlarged window (use same unique_id)
                        if (nextLen > lastIndicatorHydrationLenRef.current) {
                            try {
                                const items = activeIndicatorsRef.current;
                                const prevOldest = oldest; // previous left-edge timestamp
                                const estWarmup = (p) => {
                                    if (!p || typeof p !== 'object') return 0;
                                    const candidates = ['warmup','period','length','window','lookback','n','fast','slow'];
                                    let m = 0;
                                    for (const k of candidates) {
                                        const v = Number(p[k]);
                                        if (Number.isFinite(v)) m = Math.max(m, v);
                                    }
                                    return m;
                                };
                                for (const ind of items) {
                                    const uid = getIndicatorKey(ind.name, ind.params, symbol, timeframe, ind.paneType);
                                    sendIndicatorAction('add_indicator', {
                                        indicator: ind.name,
                                        params: ind.params,
                                        unique_id: uid,
                                        before: prevOldest,
                                        limit: unique.length,
                                        warmup: estWarmup(ind.params),
                                        pane_type: ind.paneType,
                                        scale_type: ind.scaleType,
                                    });
                                }
                                lastIndicatorHydrationLenRef.current = nextLen;
                            } catch (e) {
                                console.warn('Failed to re-request indicators for extended window:', e);
                            }
                        }
                        return { added: unique.length, total: nextLen, hasMore: !!json?.page?.has_more };
                    }
                }
                return { added: 0, total: prevLen, hasMore: !!json?.page?.has_more };
            } catch (e) {
                if (isCurrentRequest()) {
                    console.error('Cursor-based prepend failed, will fallback to count-growth:', e);
                }
            } finally {
                if (isCurrentRequest()) loadMoreInFlightRef.current = false;
            }
            if (!isCurrentRequest()) return { added: 0, total: prevLen };
        }

        // Fallback: increase count and replace
        const nextCount = Math.max(prevLen + requestStep, (lastRequestedCountRef.current || 0) + requestStep);
        if (lastRequestedCountRef.current && nextCount <= lastRequestedCountRef.current) {
            return { added: 0, total: prevLen };
        }
        loadMoreInFlightRef.current = true;
        try {
            const params = new URLSearchParams({ account_id: accountId, symbol, resolution: timeframe, count: String(nextCount) });
            const resp = await fetch(`${API_BASE_URL}/api/price/chart/history/?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!isCurrentRequest()) return { added: 0, total: prevLen };
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const json = await resp.json();
            if (!isCurrentRequest()) return { added: 0, total: prevLen };
            effectiveTimeframeRef.current = resolveEffectiveTimeframe(timeframe, json?.served_timeframe || effectiveTimeframeRef.current);
            const raw = Array.isArray(json?.candles) ? json.candles : [];
            const formatted = sortAndDeduplicateBars(raw
                .map(d => ({
                    time: toSeconds(d.time),
                    open: d.open,
                    high: d.high,
                    low: d.low,
                    close: d.close,
                    volume: (Number.isFinite(d.volume) ? Number(d.volume) : (Number.isFinite(d.tick_volume) ? Number(d.tick_volume) : (Number.isFinite(d.tickVolume) ? Number(d.tickVolume) : undefined))),
                    tickVolume: (Number.isFinite(d.tick_volume) ? Number(d.tick_volume) : (Number.isFinite(d.tickVolume) ? Number(d.tickVolume) : undefined)),
                }))
                .filter(d => typeof d.time === 'number'));
            if (formatted.length > prevLen) {
                const compressed = compressGaps(formatted, effectiveTimeframeRef.current);
                setHistoricalData(compressed);
                lastRequestedCountRef.current = formatted.length;
                historicalLenRef.current = compressed.length;
                lastBarRef.current = getLastBarSnapshot(compressed);
                setCachedChartHistory(historyCacheKey, {
                    data: compressed,
                    effectiveTimeframe: effectiveTimeframeRef.current,
                    lastRequestedCount: lastRequestedCountRef.current,
                });
                // Re-hydrate indicators for full window size
                if (formatted.length > lastIndicatorHydrationLenRef.current) {
                    try {
                        const items = activeIndicatorsRef.current;
                        for (const ind of items) {
                            const uid = getIndicatorKey(ind.name, ind.params, symbol, timeframe, ind.paneType);
                            if (ind.backendUniqueId) {
                                sendIndicatorAction('remove_indicator', { unique_id: ind.backendUniqueId });
                            }
                            sendIndicatorAction('add_indicator', {
                                indicator: ind.name,
                                params: ind.params,
                                unique_id: uid,
                                count: Math.max(1000, formatted.length),
                                pane_type: ind.paneType,
                                scale_type: ind.scaleType,
                            });
                        }
                        lastIndicatorHydrationLenRef.current = formatted.length;
                    } catch (e) {
                        console.warn('Failed to re-request indicators for full window:', e);
                    }
                }
                return { added: formatted.length - prevLen, total: formatted.length };
            }
            lastRequestedCountRef.current = Math.max(lastRequestedCountRef.current || 0, nextCount);
            return { added: 0, total: prevLen };
        } catch (e2) {
            if (isCurrentRequest()) {
                console.error('Count-growth fallback failed:', e2);
            }
            return { added: 0, total: prevLen };
        } finally {
            if (isCurrentRequest()) loadMoreInFlightRef.current = false;
        }
    }, [accountId, symbol, timeframe, historicalData]);

    // Keep ref in sync so the initial-fetch backfill can call loadMoreHistory safely
    useEffect(() => { loadMoreHistoryRef.current = loadMoreHistory; }, [loadMoreHistory]);

    const historicalDataLength = Array.isArray(historicalData) ? historicalData.length : 0;

    useEffect(() => {
        const contextKey = `${accountId || ''}|${symbol || ''}|${timeframe || ''}`;
        if (sparseBackfillContextRef.current !== contextKey) {
            sparseBackfillContextRef.current = contextKey;
            sparseBackfillAttemptsRef.current = 0;
        }

        if (!accountId || !symbol || !timeframe) return undefined;
        const currentLength = historicalDataLength;
        const effectiveTimeframe = effectiveTimeframeRef.current || timeframe;
        const minimumBars = getMinimumInitialHistoryBars(effectiveTimeframe);
        if (currentLength <= 0 || currentLength >= minimumBars) return undefined;
        if (sparseBackfillAttemptsRef.current >= MAX_SPARSE_HISTORY_BACKFILLS) return undefined;

        let cancelled = false;
        let retryTimer = null;
        const connectionToken = connectionTokenRef.current;

        const runSparseBackfill = async () => {
            if (cancelled || connectionTokenRef.current !== connectionToken) return;
            if (loadMoreInFlightRef.current) {
                retryTimer = window.setTimeout(runSparseBackfill, 500);
                return;
            }

            const beforeLength = historicalLenRef.current;
            const step = getSparseHistoryBackfillStep(effectiveTimeframeRef.current || timeframe, beforeLength);
            sparseBackfillAttemptsRef.current += 1;

            try {
                const result = await loadMoreHistoryRef.current?.(step);
                if (cancelled || connectionTokenRef.current !== connectionToken) return;
                const added = Number(result?.added) || 0;
                const afterLength = historicalLenRef.current;
                if (added <= 0 || afterLength <= beforeLength) {
                    sparseBackfillAttemptsRef.current = MAX_SPARSE_HISTORY_BACKFILLS;
                    return;
                }
                if (afterLength < minimumBars && sparseBackfillAttemptsRef.current < MAX_SPARSE_HISTORY_BACKFILLS) {
                    retryTimer = window.setTimeout(runSparseBackfill, 350);
                }
            } catch (error) {
                if (!cancelled) {
                    console.warn('Sparse chart history backfill failed:', error);
                    sparseBackfillAttemptsRef.current = MAX_SPARSE_HISTORY_BACKFILLS;
                }
            }
        };

        retryTimer = window.setTimeout(runSparseBackfill, 250);
        return () => {
            cancelled = true;
            if (retryTimer) window.clearTimeout(retryTimer);
        };
    }, [accountId, symbol, timeframe, historicalDataLength]);

    const value = useMemo(() => ({
        isConnected,
        candle,
        bid,
        ask,
        marketDepth,
        marketDepthError,
        marketDepthStatus,
        setMarketDepthEnabled,
        historicalData,
        activeIndicators,
        setActiveIndicators,
        availableIndicators,
        sendIndicatorAction,
        indicatorLiveUpdate,
        isLoggingEnabled,
        toggleLogging,
        loadMoreHistory,
        // Nested spec streams
        specStreams,
        subscribeSpec,
        unsubscribeSpec,
    }), [
        isConnected,
        candle,
        bid,
        ask,
        marketDepth,
        marketDepthError,
        marketDepthStatus,
        setMarketDepthEnabled,
        historicalData,
        activeIndicators,
        availableIndicators,
        sendIndicatorAction,
        indicatorLiveUpdate,
        isLoggingEnabled,
        toggleLogging,
        loadMoreHistory,
        specStreams,
        subscribeSpec,
        unsubscribeSpec,
    ]);
    return (
        <PriceFeedContext.Provider value={value}>
            {children}
        </PriceFeedContext.Provider>
    );
}

export default PriceFeedProvider;
